import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Clock, X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, BUSINESS } from "@/lib/sleek";
import { MaterialGalleries } from "@/components/MaterialGalleries";

const STYLE_FILTERS = ["All", "Traditional", "Transitional", "Modern"];

export default function Showroom() {
  // Gallery photos come from the DB — manage them at /admin → Showroom tab.
  const [gallery, setGallery] = useState([]);
  const [styleFilter, setStyleFilter] = useState("All");
  const [lbIdx, setLbIdx] = useState(-1);
  const [cols, setCols] = useState(() => (window.matchMedia("(min-width: 768px)").matches ? 3 : 2));

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const fn = (e) => setCols(e.matches ? 3 : 2);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  // Photos visible after applying the style filter (lightbox + grid both consume this).
  const visiblePhotos = useMemo(
    () => (styleFilter === "All" ? gallery : gallery.filter((p) => p.style === styleFilter)),
    [styleFilter, gallery]
  );

  useEffect(() => { api.get("/showroom-photos").then((r) => setGallery(r.data)).catch(() => {}); }, []);

  const lbOpen = lbIdx >= 0;
  const closeLb = useCallback(() => setLbIdx(-1), []);
  const prevLb = useCallback(() => setLbIdx((i) => (i <= 0 ? visiblePhotos.length - 1 : i - 1)), [visiblePhotos.length]);
  const nextLb = useCallback(() => setLbIdx((i) => (i >= visiblePhotos.length - 1 ? 0 : i + 1)), [visiblePhotos.length]);

  useEffect(() => {
    if (!lbOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLb();
      else if (e.key === "ArrowLeft") prevLb();
      else if (e.key === "ArrowRight") nextLb();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [lbOpen, closeLb, prevLb, nextLb]);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <img src="/photos/showroom_outside/exterior.jpg" alt="Sleek Kitchen & Bath showroom — Main Street, Northborough" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pb-20 text-white">
          <div className="text-[11px] tracking-[0.4em] uppercase opacity-90 mb-4">Sleek Kitchen &amp; Bath</div>
          <h1 className="font-display text-5xl md:text-7xl font-light leading-[1.0] max-w-4xl">
            Walk-in showroom on<br />Main Street, Northborough.
          </h1>
        </div>
      </section>

      {/* Visit strip */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-10 grid md:grid-cols-3 gap-8 text-sm">
          <div className="flex items-start gap-3">
            <MapPin size={18} className="mt-0.5" />
            <div>
              <div className="font-medium">Visit</div>
              <div className="text-zinc-600 mt-1">{BUSINESS.address}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock size={18} className="mt-0.5" />
            <div>
              <div className="font-medium">Hours</div>
              <div className="text-zinc-600 mt-1">By Appointment Only</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <ArrowUpRight size={18} className="mt-0.5" />
            <div>
              <div className="font-medium">Plan a visit</div>
              <Link to="/booking" className="text-zinc-950 underline mt-1 inline-block">Book a private showroom tour →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Inside the showroom — real photos of the Northborough space */}
      <section className="border-t border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28" data-testid="showroom-inside-gallery">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Inside the showroom</div>
              <h2 className="font-display text-4xl md:text-5xl font-light mt-4 max-w-2xl leading-tight">
                See photos from the floor.
              </h2>
            </div>
            <p className="text-zinc-600 text-sm leading-relaxed max-w-md">
              Working vignettes of cabinetry, surfaces and hardware you can touch in person. Book a
              private walk-through with our team.
            </p>
          </div>

          {/* Style filter — Traditional / Transitional / Modern */}
          <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="showroom-style-filter">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mr-3">
              Pick your style
            </div>
            {STYLE_FILTERS.map((s) => {
              const count =
                s === "All"
                  ? gallery.length
                  : gallery.filter((p) => p.style === s).length;
              const active = styleFilter === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setStyleFilter(s);
                    setLbIdx(-1);
                  }}
                  className={`px-4 py-2 text-xs tracking-[0.2em] uppercase border transition ${
                    active
                      ? "bg-zinc-950 text-white border-zinc-950"
                      : "border-zinc-300 text-zinc-700 hover:border-zinc-950"
                  }`}
                  data-testid={`showroom-style-${s.toLowerCase()}`}
                >
                  {s} <span className={`ml-1 ${active ? "text-white/60" : "text-zinc-400"}`}>{count}</span>
                </button>
              );
            })}
          </div>

          {/* Style → Slab Visualizer cross-sell removed with Slab Visualizer section */}

          {/* Row-ordered masonry — photo 1,2,3 across the first row, 4,5,6 across the second, etc. */}
          <div className={`grid ${cols === 3 ? "grid-cols-3" : "grid-cols-2"} gap-3 md:gap-4 mt-8 items-start`}>
            {Array.from({ length: cols }, (_, c) => (
              <div key={c} className="flex flex-col gap-3 md:gap-4">
                {visiblePhotos
                  .map((p, i) => ({ p, i }))
                  .filter(({ i }) => i % cols === c)
                  .map(({ p, i }) => (
                    <Fragment key={p.src}>
                    <button
                      type="button"
                      onClick={() => setLbIdx(i)}
                      className="relative w-full overflow-hidden bg-white group cursor-zoom-in block"
                      data-testid={`showroom-photo-${i}`}
                      aria-label={`Open photo ${i + 1} of ${visiblePhotos.length}`}
                    >
                      <img
                        src={p.src}
                        alt={`Sleek Kitchen & Bath showroom — ${p.style} vignette`}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-auto object-cover group-hover:scale-105 transition duration-700"
                      />
                      <span className="absolute top-2.5 left-2.5 bg-white/90 text-zinc-950 text-[9px] tracking-[0.2em] uppercase px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {p.style}
                      </span>
                      {styleFilter === "All" && i === 0 && (
                        <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4 text-left">
                          <span className="text-white text-[10px] tracking-[0.35em] uppercase">The main floor · Northborough</span>
                        </span>
                      )}
                    </button>
                    {/* Story cards woven between photos — they don't occupy numbered photo slots */}
                    {styleFilter === "All" && i === 2 && (
                      <div className="bg-zinc-950 text-white p-7 md:p-8" data-testid="showroom-gallery-card-styles">
                        <div className="font-display text-5xl font-light">80+</div>
                        <p className="text-zinc-400 text-sm mt-3 leading-relaxed">
                          door styles, finishes and hardware pulls on the floor — open every drawer, feel every hinge.
                        </p>
                      </div>
                    )}
                    {styleFilter === "All" && i === 9 && (
                      <div className="bg-zinc-950 text-white p-7 md:p-8" data-testid="showroom-gallery-card-tour">
                        <div className="font-display text-2xl font-light leading-snug">See it.<br />Touch it.<br />Spec it.</div>
                        <Link
                          to="/booking"
                          className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b border-white/60 pb-1 hover:gap-3 transition-all"
                          data-testid="showroom-gallery-card-tour-cta"
                        >
                          Book a private tour <ArrowUpRight size={13} />
                        </Link>
                      </div>
                    )}
                    {styleFilter === "All" && i === 13 && (
                      <div className="border border-zinc-300 bg-white p-7 md:p-8" data-testid="showroom-gallery-card-visualizer">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Can't picture it at home?</div>
                        <p className="font-display text-xl font-light mt-3 leading-snug">Upload a photo of your room and restyle it with AI.</p>
                        <Link
                          to="/visualizer"
                          className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-zinc-950 border-b border-zinc-950 pb-1 hover:gap-3 transition-all"
                          data-testid="showroom-gallery-card-visualizer-cta"
                        >
                          Try the visualizer <ArrowUpRight size={13} />
                        </Link>
                      </div>
                    )}
                  </Fragment>
                  ))}
              </div>
            ))}
          </div>

          {visiblePhotos.length === 0 && (
            <div className="mt-12 text-center text-sm text-zinc-500" data-testid="showroom-style-empty">
              No photos in this style yet — try another filter.
            </div>
          )}

          <div className="mt-12 text-center">
            <Link
              to="/booking"
              className="inline-flex items-center gap-2 bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition"
              data-testid="showroom-inside-cta"
            >
              Book a private showroom tour <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Cabinet Brands — showroom partnerships */}
      <section className="border-t border-zinc-200 bg-zinc-50" data-testid="showroom-cabinet-brands">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-500">Cabinet partners</div>
            <h2 className="font-display text-3xl md:text-4xl font-light mt-4 leading-tight">
              Premium cabinetry, on display in Northborough.
            </h2>
            <p className="mt-5 text-zinc-600 leading-relaxed text-sm md:text-base">
              We install lines from three of the most respected cabinet vendors in North America —
              touch the doors, see the finishes, and spec your kitchen in person.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 items-stretch">
            {[
              { name: "Candlelight Cabinetry", logo: "/photos/cabinet_brands/candlelight.png", url: "https://candlelightcab.com", blurb: "High-quality semi-custom to-custom cabinetry in upstate New York. Traditional & inset doors." },
              { name: "Showplace Cabinetry", logo: "/photos/cabinet_brands/showplace.png", url: "https://showplacecabinetry.com", blurb: "Employee-owned in South Dakota. Custom finishes and lots of options." },
              { name: "Fabuwood Cabinetry", logo: "/photos/cabinet_brands/fabuwood.png", url: "https://www.fabuwood.com", blurb: "Modern & transitional lines. Quick-ship for tight timelines." },
            ].map((brand) => (
              <a
                key={brand.name}
                href={brand.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white border border-zinc-200 hover:border-zinc-900 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg p-8 flex flex-col items-center text-center"
                data-testid={`cabinet-brand-${brand.name.toLowerCase().split(" ")[0]}`}
              >
                <div className="h-20 md:h-24 flex items-center justify-center w-full">
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    loading="lazy"
                    decoding="async"
                    className="max-h-full max-w-[180px] md:max-w-[200px] object-contain opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </div>
                <p className="mt-5 text-xs md:text-sm text-zinc-600 leading-relaxed">{brand.blurb}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-zinc-500 group-hover:text-zinc-900 transition">
                  Visit brand <ArrowUpRight size={12} />
                </span>
              </a>
            ))}
          </div>

          <p className="mt-10 text-center text-xs text-zinc-500">
            Brand sites open in a new tab — your Sleek session stays right here.
          </p>
        </div>
      </section>

      {/* Countertops & Materials — partnership feature */}
      <section className="border-t border-zinc-200 bg-white" data-testid="showroom-materials-feature">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7">
            <div className="grid grid-cols-3 gap-3">
              <Link to="/showroom/materials" className="group block overflow-hidden" data-testid="showroom-slab-image-quartz" aria-label="View countertop options">
                <img src="/photos/materials/quartz.jpg" alt="Quartz countertops" loading="lazy" decoding="async" className="aspect-[3/4] object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
              </Link>
              <Link to="/showroom/materials" className="group block overflow-hidden" data-testid="showroom-slab-image-quartzite" aria-label="View countertop options">
                <img src="/photos/materials/quartzite.jpg" alt="Quartzite countertops" loading="lazy" decoding="async" className="aspect-[3/4] object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
              </Link>
              <Link to="/showroom/materials" className="group block overflow-hidden" data-testid="showroom-slab-image-marble" aria-label="View countertop options">
                <img src="/photos/materials/marble.jpg" alt="Marble countertops" loading="lazy" decoding="async" className="aspect-[3/4] object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" />
              </Link>
            </div>
            <Link
              to="/showroom/materials"
              className="mt-5 inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-zinc-600 hover:text-zinc-950 border-b border-zinc-300 hover:border-zinc-950 pb-1 transition"
              data-testid="showroom-slab-options-link"
            >
              Browse countertop &amp; slab options <ArrowUpRight size={13} />
            </Link>
          </div>
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.4em] uppercase text-zinc-500">Countertops &amp; surfaces</div>
            <h2 className="font-display text-4xl md:text-5xl font-light mt-4 leading-tight">
              Quartz, granite &amp; stone — fabricated in-house.
            </h2>
            <p className="mt-6 text-zinc-600 leading-relaxed">
              Through our showroom partnership with Dream Stoneworks, samples are selected
              here in Northborough and cut at the Hudson, MA fabrication facility. Compare
              quartz, granite, marble, quartzite, Dekton, and Neolith for kitchens, islands,
              vanity tops, fireplaces, desk tops and more.
            </p>
            <Link
              to="/showroom/materials"
              className="mt-8 inline-flex items-center gap-2 bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition"
              data-testid="showroom-materials-cta"
            >
              Explore countertop materials <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Material photo galleries */}
      <MaterialGalleries />

      {/* Showroom lightbox */}
      {lbOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={`Showroom photo ${lbIdx + 1} of ${visiblePhotos.length}`}
          onClick={(e) => { if (e.target === e.currentTarget) closeLb(); }}
          data-testid="showroom-lightbox"
        >
          <button
            type="button"
            onClick={closeLb}
            className="absolute top-5 right-5 md:top-7 md:right-7 text-white/80 hover:text-white p-2"
            aria-label="Close gallery"
            data-testid="showroom-lightbox-close"
          >
            <X size={28} />
          </button>
          <div
            className="absolute top-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.3em] uppercase"
            data-testid="showroom-lightbox-counter"
          >
            {lbIdx + 1} / {visiblePhotos.length}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); prevLb(); }}
            className="absolute left-3 md:left-6 text-white/70 hover:text-white p-3 md:p-4"
            aria-label="Previous photo"
            data-testid="showroom-lightbox-prev"
          >
            <ChevronLeft size={36} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); nextLb(); }}
            className="absolute right-3 md:right-6 text-white/70 hover:text-white p-3 md:p-4"
            aria-label="Next photo"
            data-testid="showroom-lightbox-next"
          >
            <ChevronRight size={36} />
          </button>
          <img
            src={visiblePhotos[lbIdx]?.src}
            alt={`Sleek Kitchen & Bath showroom — view ${lbIdx + 1}`}
            className="max-w-[92vw] max-h-[82vh] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            data-testid="showroom-lightbox-img"
          />
          <div className="absolute bottom-0 left-0 right-0 px-6 pb-5 md:pb-6 flex items-center justify-between gap-4">
            <div className="text-white/75 text-xs tracking-[0.25em] uppercase truncate">
              Sleek Kitchen &amp; Bath Showroom
            </div>
            <Link
              to="/booking?ref=showroom&title=Showroom%20Tour&cat=Showroom%20Visit"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-2 bg-white text-zinc-950 px-4 md:px-5 py-2.5 md:py-3 text-xs tracking-[0.2em] uppercase hover:bg-zinc-200 transition whitespace-nowrap"
              data-testid="showroom-lightbox-cta"
            >
              Book a tour
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

